import {
  getWindowDisplayId,
  getWindowRoleSync,
} from "@/desktop/use-window-role";
import { getCurrentOwnerEpoch } from "@/lib/owner-state-client";
import {
  resolvePresentationDragPath,
  resolvePresentationFrameDisplayId,
  resolvePresentationPoint,
  resolvePresentationRect,
} from "./agent-presentation-contract";
import type {
  AgentPresentation,
  AgentPresentationStep,
} from "./agent-presentation-types";
import { recordSessionLatency } from "./session-latency-trace";

function targetDisplayId(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): number | null | undefined {
  if (
    step.kind === "point" ||
    step.kind === "click" ||
    step.kind === "hover" ||
    step.kind === "paste" ||
    step.kind === "scroll" ||
    step.kind === "key"
  ) {
    return resolvePresentationPoint(step, presentation)?.displayId;
  }
  if (step.kind === "highlight" || step.kind === "draw") {
    return resolvePresentationRect(step, presentation)?.displayId;
  }
  if (step.kind === "drag") {
    return resolvePresentationDragPath(step, presentation)?.[0]?.displayId;
  }
  if (step.kind === "frame") {
    return resolvePresentationFrameDisplayId(step, presentation);
  }
  return "target_display_id" in step &&
    typeof step.target_display_id === "number"
    ? step.target_display_id
    : null;
}

export function rendersInCurrentWindow(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
): boolean {
  const target = targetDisplayId(presentation, step);
  const current = getWindowDisplayId();
  return target != null
    ? current != null && current === target
    : getWindowRoleSync() === "owner";
}

export function tracePresentationDelivery(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  phase: string,
  outcome: string,
): void {
  const target = targetDisplayId(presentation, step);
  const captureTarget =
    ("capture_rect" in step && step.capture_rect) ||
    ("capture_point" in step && step.capture_point) ||
    ("capture_path" in step && step.capture_path);
  recordSessionLatency("presentation.delivery", {
    presentationId: presentation.presentationId,
    executionId: presentation.executionId,
    stepId: step.step_id,
    displayId: getWindowDisplayId(),
    targetDisplayId: target ?? null,
    ownerEpoch: getCurrentOwnerEpoch(),
    scope: getWindowRoleSync(),
    quality:
      target != null
        ? "target_resolved"
        : captureTarget
          ? "capture_target_unresolved"
          : "owner_default",
    phase,
    outcome,
  });
}

export function tracePresentationSequence(
  data: Record<string, unknown>,
  outcome: string,
  reason?: string,
): void {
  recordSessionLatency("presentation.sequence", {
    presentationId:
      typeof data.presentation_id === "string"
        ? data.presentation_id
        : undefined,
    executionId:
      typeof data.execution_id === "string" ? data.execution_id : undefined,
    displayId: getWindowDisplayId(),
    ownerEpoch: getCurrentOwnerEpoch(),
    scope: getWindowRoleSync(),
    count: Array.isArray(data.steps) ? data.steps.length : undefined,
    outcome: reason ? `${outcome}:${reason}` : outcome,
  });
}

export function tracePresentationCancellation(
  presentation: AgentPresentation,
  reason: string,
): void {
  recordSessionLatency("presentation.cancel_requested", {
    presentationId: presentation.presentationId,
    executionId: presentation.executionId,
    displayId: getWindowDisplayId(),
    ownerEpoch: getCurrentOwnerEpoch(),
    scope: getWindowRoleSync(),
    outcome: reason,
  });
}
